import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from './components/Icon';

/**
 * ComboBox — pole wpisywania z podpowiedziami (wzorzec WAI-ARIA 1.2 „combobox + listbox").
 * B3 (spotkanie z Bronkiem 21.10): wybór zarządcy z ~70 pozycji przez przewijanie długiej listy był męczący —
 * teraz wpisujesz kilka liter nazwy albo skrótu i wybierasz z podpowiedzi.
 *
 *  • dopasowanie: każde wpisane słowo musi wystąpić w etykiecie albo w `keywords` (np. skrót zarządcy) — bez
 *    rozróżniania wielkości liter i polskich znaków („zabrz" = „Zabrzańska", „ZBM" = „zbm");
 *  • klawiatura: ↓/↑ — podpowiedzi, Enter — wybór, Esc — zamknij listę i przywróć wybór (okno pod spodem zostaje
 *    otwarte), Tab/wyjście z pola — przywraca wybór; wyczyszczenie tekstu i wyjście = brak wyboru (gdy `emptyLabel`);
 *  • `emptyLabel` (np. „— wszyscy —") = pozycja „bez wyboru" na górze listy i placeholder pustego pola.
 * Wyszukiwanie po stronie klienta — lista opcji jest w pamięci (zarządcy: ~70 na prod, 2026-10-22).
 */
export interface ComboBoxOption {
  value: string;
  label: string;
  /** Dodatkowe teksty, po których opcja też się znajduje (skrót, NIP…) — niewidoczne. */
  keywords?: Array<string | null | undefined>;
}

export interface ComboBoxProps {
  label: string;
  options: ComboBoxOption[];
  /** '' = brak wyboru. */
  value: string;
  onChange: (value: string) => void;
  /** Pozycja „bez wyboru” (value '') na górze listy + placeholder pustego pola. Bez niej pola nie da się wyczyścić. */
  emptyLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  hint?: React.ReactNode;
  containerStyle?: React.CSSProperties;
  /** Ile podpowiedzi renderować naraz (reszta po doprecyzowaniu frazy). Domyślnie 60. */
  maxResults?: number;
  noMatchText?: string;
  'data-testid'?: string;
}

const PL: Record<string, string> = { ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z' };

/** Tekst do porównań: małe litery, bez polskich (i innych) znaków diakrytycznych, pojedyncze spacje. */
export function foldSearchText(s: unknown): string {
  return String(s ?? '')
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (c) => PL[c] || c)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Opcje pasujące do frazy (każde słowo = fragment etykiety lub keywords). Najpierw te, których etykieta albo
 * keyword ZACZYNA SIĘ od frazy, potem reszta — w obu grupach kolejność wejściowa. Pusta fraza = wszystkie.
 */
export function filterComboOptions(options: ComboBoxOption[], query: string): ComboBoxOption[] {
  const q = foldSearchText(query);
  if (!q) return options;
  const words = q.split(' ');
  const starts: ComboBoxOption[] = [];
  const rest: ComboBoxOption[] = [];
  for (const o of options) {
    const fields = [o.label, ...(o.keywords || [])].map(foldSearchText).filter(Boolean);
    const blob = fields.join(' ');
    if (!words.every((w) => blob.includes(w))) continue;
    (fields.some((f) => f.startsWith(q)) ? starts : rest).push(o);
  }
  return [...starts, ...rest];
}

const visuallyHidden: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

export function ComboBox({
  label, options, value, onChange, emptyLabel, placeholder, disabled, id, hint, containerStyle,
  maxResults = 60, noMatchText = 'Brak dopasowań', 'data-testid': testId,
}: ComboBoxProps) {
  const reactId = useId();
  const inputId = id ?? `combobox-${reactId}`;
  const listId = `${inputId}-listbox`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected ? selected.label : '';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selectedLabel);
  const [typed, setTyped] = useState(false); // filtr dopiero po wpisaniu — po otwarciu widać całą listę
  const [active, setActive] = useState(-1);
  const [focused, setFocused] = useState(false);

  // Wybór zmieniony z zewnątrz (albo dociągnięte opcje) → pole pokazuje aktualną etykietę, gdy nikt w nim nie pisze.
  useEffect(() => {
    if (!open) setQuery(selectedLabel);
  }, [selectedLabel, open]);

  const items = useMemo<ComboBoxOption[]>(() => {
    const matched = typed ? filterComboOptions(options, query) : options;
    const head = emptyLabel !== undefined && (!typed || !foldSearchText(query)) ? [{ value: '', label: emptyLabel }] : [];
    return [...head, ...matched].slice(0, maxResults + head.length);
  }, [options, query, typed, emptyLabel, maxResults]);
  const totalMatches = typed ? filterComboOptions(options, query).length : options.length;

  const optionId = (i: number) => `${inputId}-opt-${i}`;

  const close = useCallback(() => {
    setOpen(false);
    setTyped(false);
    setActive(-1);
  }, []);
  const revert = useCallback(() => {
    close();
    setQuery(selectedLabel);
  }, [close, selectedLabel]);
  const choose = useCallback((o: ComboBoxOption) => {
    close();
    setQuery(o.value ? o.label : '');
    if (o.value !== value) onChange(o.value);
  }, [close, onChange, value]);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    const i = items.findIndex((o) => o.value === value);
    setActive(i >= 0 ? i : 0);
  };

  // Esc: ModalShell łapie Escape w fazie capture na `document` i zamyka całe okno. Gdy lista jest otwarta,
  // przechwytujemy go wcześniej (capture na `window`) — Esc zamyka tylko listę.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.target !== inputRef.current) return;
      e.stopPropagation();
      e.preventDefault();
      revert();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, revert]);

  useEffect(() => {
    if (!open || active < 0) return;
    const el = document.getElementById(optionId(active));
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { openList(); return; }
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { openList(); return; }
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault(); // bez wysyłania formularza / domyślnej akcji okna
      const o = items[active];
      if (o) choose(o);
    } else if (e.key === 'Tab') {
      if (open) leave();
    }
  };

  // Wyjście z pola (Tab albo klik obok): pusty tekst przy dozwolonym „bez wyboru” = świadome wyczyszczenie;
  // inaczej wracamy do wybranej pozycji.
  const leave = () => {
    if (emptyLabel !== undefined && typed && !foldSearchText(query)) {
      close();
      if (value !== '') onChange('');
      return;
    }
    revert();
  };
  const onBlur = () => {
    setFocused(false);
    if (open) leave();
  };

  const border = focused ? 'var(--action-primary)' : 'var(--border-strong)';
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)', ...containerStyle }}>
      <label
        htmlFor={inputId}
        style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)' }}
      >
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && active >= 0 && items[active] ? optionId(active) : undefined}
          aria-describedby={hintId}
          disabled={disabled}
          value={query}
          placeholder={placeholder ?? emptyLabel}
          onFocus={(e) => { setFocused(true); e.target.select(); openList(); }}
          onClick={() => { if (!open) openList(); }}
          onChange={(e) => { setQuery(e.target.value); setTyped(true); setOpen(true); setActive(0); }}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          style={{
            width: '100%',
            minHeight: 'var(--touch-min)',
            padding: `0 ${value && emptyLabel !== undefined ? 'calc(var(--space-4) + 28px)' : 'var(--space-4)'} 0 var(--space-4)`,
            boxSizing: 'border-box',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-body)',
            color: 'var(--text-primary)',
            background: 'var(--surface-card)',
            border: `var(--border-width-strong) solid ${border}`,
            borderRadius: 'var(--radius-md)',
            boxShadow: focused ? 'var(--focus-ring)' : 'none',
            outline: 'none',
          }}
        />
        {value && emptyLabel !== undefined && !disabled && (
          <button
            type="button"
            aria-label={`Wyczyść: ${label}`}
            title="Wyczyść"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { close(); setQuery(''); onChange(''); inputRef.current?.focus(); }}
            style={{
              position: 'absolute', right: 'var(--space-2)', top: '50%', transform: 'translateY(-50%)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28,
              padding: 0, border: 'none', borderRadius: 'var(--radius-md)', background: 'transparent',
              color: 'var(--text-muted)', cursor: 'pointer',
            }}
          >
            <Icon name="x" size={18} />
          </button>
        )}
        <ul
          id={listId}
          role="listbox"
          aria-label={`Podpowiedzi: ${label}`}
          hidden={!open}
          style={{
            position: 'absolute', zIndex: 20, left: 0, right: 0, top: 'calc(100% + 4px)', margin: 0, padding: 'var(--space-1) 0',
            listStyle: 'none', maxHeight: 280, overflowY: 'auto', background: 'var(--surface-card)',
            border: 'var(--border-width) solid var(--border-strong)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)',
          }}
        >
          {items.map((o, i) => (
            <li
              key={o.value || '__empty__'}
              id={optionId(i)}
              role="option"
              aria-selected={o.value === value}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(o)}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                cursor: 'pointer',
                fontSize: 'var(--text-body)',
                color: o.value ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: o.value === value ? 'var(--weight-semibold)' : 'var(--weight-regular)',
                background: i === active ? 'var(--surface-hover)' : 'transparent',
              }}
            >
              {o.label}
            </li>
          ))}
          {open && items.filter((o) => o.value).length === 0 && (
            <li role="presentation" style={{ padding: 'var(--space-2) var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>
              {noMatchText}
            </li>
          )}
          {open && totalMatches > maxResults && (
            <li role="presentation" style={{ padding: 'var(--space-2) var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              {`Pokazano ${maxResults} z ${totalMatches} — wpisz więcej liter.`}
            </li>
          )}
        </ul>
        <span role="status" aria-live="polite" style={visuallyHidden}>
          {open && typed ? (totalMatches === 0 ? noMatchText : `Podpowiedzi: ${totalMatches}`) : ''}
        </span>
      </div>
      {hint && (
        <span id={hintId} style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{hint}</span>
      )}
    </div>
  );
}

export default ComboBox;
